import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Document, Thumbnail } from "react-pdf";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { FloatingPortal } from "@floating-ui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { FileAttachmentIcon } from "@hugeicons-pro/core-stroke-rounded";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { Show } from "shared/components";
import { Loader } from "shared/components/Loader";
import { AssetViewer } from "shared/app/AssetViewer";
import { cx } from "shared/utils";
import { NoteAttachment } from "../types";

const displayExtensions = [
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      target: "_blank",
      rel: "noopener noreferrer"
    }
  }),
  Underline
];

const AttachmentThumbnail = ({
  attachment,
  isLoading,
  isError
}: {
  attachment: NoteAttachment;
  isLoading: boolean;
  isError: boolean;
}) => {
  const isImage = attachment.content_type.startsWith("image/");

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <HugeiconsIcon
        icon={FileAttachmentIcon}
        size={40}
        strokeWidth={1}
        className="text-content-200"
      />
    );
  if (isImage && attachment.url)
    return (
      <img
        src={attachment.url}
        alt={attachment.original_filename}
        className="w-full h-full object-contain"
      />
    );
  return (
    <Document
      key={`note_attachment_pdf_${attachment.url}`}
      file={attachment.url}
      className="pointer-events-none flex h-full w-full items-center justify-center"
      loading={<Loader />}
    >
      <Thumbnail height={154} pageNumber={1} />
    </Document>
  );
};

const AttachmentCard = ({
  attachment,
  onAttachmentClick
}: {
  attachment: NoteAttachment;
  onAttachmentClick?: (attachment: NoteAttachment) => void;
}) => {
  const [isAssetViewerOpen, setIsAssetViewerOpen] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryFn: () => fetch(attachment.url).then((response) => response.blob()),
    queryKey: ["NOTE_ATTACHMENT_CARD", attachment.url]
  });

  const handleClick = () => {
    if (onAttachmentClick) onAttachmentClick(attachment);
    else setIsAssetViewerOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading || isError}
        className="block w-[152px] rounded-lg border border-alpha-100 overflow-hidden hover:border-alpha-200 transition-colors"
      >
        <div className="h-[154px] bg-background-900 flex items-center justify-center overflow-hidden">
          <AttachmentThumbnail
            attachment={attachment}
            isLoading={isLoading}
            isError={isError}
          />
        </div>
        <div className="px-2 py-3 bg-background-1000">
          <p className="text-xs font-medium text-content-1000 truncate">
            {attachment.original_filename}
          </p>
        </div>
      </button>
      {!onAttachmentClick && data ? (
        <AssetViewer
          src={attachment.url}
          filename={attachment.original_filename}
          open={isAssetViewerOpen}
          onClose={() => setIsAssetViewerOpen(false)}
          mimeType={attachment.content_type || data.type}
          fileSize={attachment.size || data.size}
        />
      ) : null}
    </>
  );
};

interface NoteDisplayProps {
  content: string;
  attachments?: NoteAttachment[];
  containerClassName?: string;
  onAttachmentClick?: (attachment: NoteAttachment) => void;
}

type LinkTooltip = {
  url: string;
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
};

const buildTooltip = (anchor: HTMLAnchorElement): LinkTooltip => {
  const rect = anchor.getBoundingClientRect();
  const TOOLTIP_WIDTH = 332;
  const TOOLTIP_HEIGHT = 80;
  const GAP = 6;
  const EDGE = 8;
  const hasSpaceOnRight = rect.left + TOOLTIP_WIDTH <= window.innerWidth - EDGE;
  const hasSpaceBelow =
    rect.bottom + GAP + TOOLTIP_HEIGHT <= window.innerHeight - EDGE;
  return {
    url: anchor.getAttribute("href") ?? "",
    ...(hasSpaceOnRight
      ? { left: rect.left }
      : { right: window.innerWidth - rect.right }),
    ...(hasSpaceBelow
      ? { top: rect.bottom + GAP }
      : { bottom: window.innerHeight - rect.top + GAP })
  };
};

export const NoteDisplay = ({
  content,
  attachments,
  containerClassName,
  onAttachmentClick
}: NoteDisplayProps) => {
  const [tooltip, setTooltip] = useState<LinkTooltip | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mousePosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleScroll = () => {
      const el = document.elementFromPoint(
        mousePosRef.current.x,
        mousePosRef.current.y
      );
      const anchor = el?.closest("a") as HTMLAnchorElement | null;
      if (anchor && containerRef.current?.contains(anchor))
        setTooltip(buildTooltip(anchor));
      else setTooltip(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const handleMouseOver = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (anchor) setTooltip(buildTooltip(anchor as HTMLAnchorElement));
    else setTooltip(null);
  }, []);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  return (
    <div
      ref={containerRef}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <LiteTextEditor
        editable={false}
        initialValue={content}
        id="activity-note"
        extensions={displayExtensions}
        containerClassName={cx("text-content-400 text-sm", containerClassName)}
      />
      <Show if={attachments?.length}>
        <div className="flex flex-wrap gap-3 mt-4">
          {attachments?.map((attachment) => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              onAttachmentClick={onAttachmentClick}
            />
          ))}
        </div>
      </Show>
      {tooltip ? (
        <FloatingPortal>
          <div
            style={{
              position: "fixed",
              top: tooltip.top,
              bottom: tooltip.bottom,
              left: tooltip.left,
              right: tooltip.right,
              zIndex: 105
            }}
            className="flex flex-row items-center justify-center gap-2.5 p-4 max-w-[332px] rounded-[11px] border border-white/40 bg-alpha-800 backdrop-blur-[7px] shadow-[0px_4px_24px_rgba(0,0,0,0.15)] text-sm text-content-1000-inverted break-all pointer-events-none"
          >
            {tooltip.url}
          </div>
        </FloatingPortal>
      ) : null}
    </div>
  );
};
